import { defineStore, storeToRefs } from 'pinia';
import { getRecentlySelectedScope, setRecentlySelectedScope } from '../shared/helpers/scopefilterHelpers.ts';
import { computed, ref, watch } from 'vue';
import { useScopeFilterStore } from './scopeFilterStore.ts';
import { getScopes, type ScopeWithDescription } from '@/shared/helpers/api.ts';
import { http } from '../shared/httpWrapper.js';

export const useScopeStore = defineStore('scope', () => {
    const selectedScope = ref<string | null>(null);
    const scopes = ref<ScopeWithDescription[]>([]);
    const scopesLoading = ref(true);
    const lastUpdated = ref<Date | null>(null);

    const scopeFilterStore = useScopeFilterStore();
    const { selectedScopeFilter, scopeType } = storeToRefs(scopeFilterStore);

    const filteredScopes = computed(() => {
        if (scopeFilterStore.selectedScopeFilter === null || scopeFilterStore.selectedScopeFilter.length === 0) {
            return scopes.value;
        } else {
            return scopes.value.filter(scp => scopeFilterStore.selectedScopeFilter.includes(scp.identity));
        }
    });

    const selectedScopeLabel = computed(() => {
        const defaultLabel = `Select ${scopeType.value}`;

        return selectedScope.value ? selectedScope.value : defaultLabel;
    });

    watch(selectedScopeFilter, () => {
        if (scopeFilterStore.selectedScopeFilter?.length === 1) {
            const scope = scopeFilterStore.selectedScopeFilter[0];
            if (!selectedScope.value) {
                updateSelectedScope(scope);
            }
        } else if (scopeFilterStore.selectedScopeFilter?.length === 0) {
            selectedScope.value = null;
        }
    });

    watch(selectedScope, (newValue) => {
        setRecentlySelectedScope(scopeType.value, newValue);
    });

    function updateSelectedScope(scope: string | null) {
        setRecentlySelectedScope(scopeType.value, scope);
        selectedScope.value = scope;
    }

    function updateScopes(scp: ScopeWithDescription[]) {
        scopes.value = scp;
    }

    function getSelectedScope() {
        return selectedScope.value;
    }

    function setScopeType(st: string | null) {
        scopeFilterStore.setScopeType(st);
    }

    async function init() {
        scopesLoading.value = true;

        const timediff = lastUpdated.value ? (Date.now() - lastUpdated.value.getTime()) / 1000 : Infinity;
        const cacheAgeInSeconds = 30;
        const scopeType = scopeFilterStore.scopeType;

        if (timediff > cacheAgeInSeconds) {
            const recentlySelectedScope = getRecentlySelectedScope(scopeType);
            if (recentlySelectedScope !== null) {
                selectedScope.value = recentlySelectedScope;
            }

            const scopeWithDesc = await getScopes(http, scopeType ?? '');
            updateScopes(scopeWithDesc);
        }
        scopesLoading.value = false;
    }

    return {
        selectedScope,
        scopes,
        scopesLoading,
        lastUpdated,
        scopeType,
        selectedScopeLabel,
        updateSelectedScope,
        updateScopes,
        filteredScopes,
        getSelectedScope,
        setScopeType,
        init
    };
});
